import { Component, OnInit } from '@angular/core';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import {
  PcCycle,
  PcSimulationResults,
  SimulationResponse,
  SimulationResults,
} from '../../../core/models/simulation.model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import Highcharts from 'highcharts';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { map, merge } from 'rxjs';
import { createBaseLinearChartOptions, createLineSeries, updateChartSeriesVisibilitySmooth } from '../../../utils/chart/chart-config.util';
import { formatValue } from '../../../utils/format.util';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';
import { SimulationFilterResult } from '../../../core/models/simulation.model';

@Component({
  selector: 'app-capillary-pressure-results',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatExpansionModule,
    HighchartsChartModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatTableModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent,
  ],
  templateUrl: './capillary-pressure-results.component.html',
  styleUrl: './capillary-pressure-results.component.scss',
  standalone: true,
})
export class CapillaryPressureResultsComponent extends Destroyable(Object) implements OnInit {
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly componentId: string = 'pc-simulation-results';
  readonly primaryDrainageColor: string = '#4F81BD';
  readonly firstImbibitionColor: string = '#C0504D';
  readonly secondaryDrainageColor: string = '#8064A2';
  readonly defaultColor: string = '#4A4A4A';
  readonly cycles: PcCycle[] = ['d1', 'imb', 'd2'];
  simulationResponse: SimulationResponse<PcSimulationResults> | null = null;
  cyclesForm!: FormGroup;
  chart?: Highcharts.Chart;
  chartOptions!: Highcharts.Options;
  displayedColumns: string[] = [];
  tableData: any[] = [];
  isLoading: boolean = false;
  relativePermeability: string | null = null;
  relativePermeabilityId: string | null = null;
  saturationExponent: string | null = null;
  saturationExponentId: string | null = null;

  get pageTitle(): string {
    const prefix =
      this.simulationResponse?.type === SimulatorType.UpscalingPC
        ? 'Upscaling - '
        : 'Subsample - ';
    return prefix + 'Capillary Pressure Results';
  }

  get isUpscaling(): boolean {
    return this.simulationResponse?.type === SimulatorType.UpscalingPC;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initCyclesForm();

    const simulationId: string | null = this.route.snapshot.paramMap.get('id');
    if (simulationId) {
      this.loadSimulation(simulationId);
    }
  }

  get results(): PcSimulationResults[] | undefined {
    return this.simulationResponse?.results;
  }

  get simulationResult(): PcSimulationResults | undefined {
    return this.results?.[0];
  }

  get simulationDirection(): string {
    return this.simulationResult?.direction ?? '';
  }

  get swd1Value(): string[] {
    return this.simulationResult?.result?.swD1 ?? [];
  }

  get pcd1Value(): string[] {
    return this.simulationResult?.result?.pcD1 ?? [];
  }

  get swImbValue(): string[] {
    return this.simulationResult?.result?.swImb ?? [];
  }

  get pcImbValue(): string[] {
    return this.simulationResult?.result?.pcImb ?? [];
  }

  get swd2Value(): string[] {
    return this.simulationResult?.result?.swD2 ?? [];
  }

  get pcd2Value(): string[] {
    return this.simulationResult?.result?.pcD2 ?? [];
  }

  getCycleControl(cycle: PcCycle): FormControl {
    return this.cyclesForm.get(cycle) as FormControl;
  }

  isPcCurveDataValid(): boolean {
    return !!this.results &&
      this.results.length > 0;
  }

  isSimulationResultValid(): boolean {
    return this.simulationResult?.status?.toLowerCase() === 'success';
  }

  isCycleResultExists(cycle: PcCycle): boolean {
    if (!this.simulationResult) return false;

    switch (cycle) {
      case 'd1':
        return Boolean(this.pcd1Value.length && this.swd1Value.length);

      case 'imb':
        return Boolean(this.pcImbValue.length && this.swImbValue.length);

      case 'd2':
        return false; // Boolean(this.pcd2Value.length && this.swd2Value.length);
    }
  }

  saveChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    // Initialize chart with all series upfront for smooth visibility toggling
    this.initializeChartSeries();
  }

  private initializeChartSeries(): void {
    if (!this.chart) return;

    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];

    // Add all possible series to the chart upfront
    cycleOrder.forEach((cycle) => {
      if (this.isCycleResultExists(cycle)) {
        const series = this.createChartSeriesByCycleType(cycle);
        this.chart!.addSeries(series, false);
      }
    });

    this.chart.redraw();

    // Apply initial visibility based on form state
    this.updateChartSeriesVisibilitySmooth();
  }

  getColumnStyle(columnName: string): Record<string, string> {
    return {
      'background': this.defineColorForTable(columnName),
      'width': `${100 / (this.displayedColumns.length || 1)}%`,
    };
  }

  downloadResults(): void {
    const directions: Record<string, boolean> = this.getSelectedDirections();
    this.simulationsService.downloadResult(this.simulationResponse!.id, directions);
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation: SimulationResponse<SimulationResults>) => {
        this.simulationResponse = simulation as SimulationResponse<PcSimulationResults>;

        if (this.isPcCurveDataValid() && this.isSimulationResultValid()) {
          this.configureCyclesFormControls();
          this.initChart();
          this.updateTable();
        }

        // Load child simulations (Relative Permeability and Saturation Exponent)
        this.loadChildSimulations(id);
      });
  }

  openChildSimulation(childSimulationId: string, type: 'kr' | 'n'): void {
    const routePath = type === 'kr' ? '/simulation-results/kr' : '/simulation-results/n';
    const url = this.router.serializeUrl(
      this.router.createUrlTree([routePath, childSimulationId])
    );
    window.open(url, '_blank');
  }

  private loadChildSimulations(parentSimulationId: string): void {
    console.log('Loading child simulations for parent ID:', parentSimulationId);

    // Determine if this is an upscaling simulation
    const isUpscaling = this.simulationResponse?.type === SimulatorType.UpscalingPC;
    const krType = isUpscaling ? SimulatorType.UpscalingKr : SimulatorType.SubsampleKr;
    const nType = isUpscaling ? SimulatorType.UpscalingN : SimulatorType.SubsampleN;

    // Load Relative Permeability (SubsampleKr or UpscalingKr)
    this.simulationsService
      .getChildSimulationsByParentId(parentSimulationId, krType, 1, 1)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (result: SimulationFilterResult<SimulationResponse<SimulationResults>>) => {
          console.log('Relative Permeability result:', result);
          if (result.data && result.data.length > 0) {
            this.relativePermeability = result.data[0].name;
            this.relativePermeabilityId = result.data[0].id;
            console.log('Found Relative Permeability:', this.relativePermeability);
          } else {
            console.log('No Relative Permeability simulations found');
          }
        },
        error: (error) => {
          console.error('Error loading Relative Permeability:', error);
          this.relativePermeability = null;
          this.relativePermeabilityId = null;
        }
      });

    // Load Saturation Exponent (SubsampleN or UpscalingN)
    this.simulationsService
      .getChildSimulationsByParentId(parentSimulationId, nType, 1, 1)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (result: SimulationFilterResult<SimulationResponse<SimulationResults>>) => {
          console.log('Saturation Exponent result:', result);
          if (result.data && result.data.length > 0) {
            this.saturationExponent = result.data[0].name;
            this.saturationExponentId = result.data[0].id;
            console.log('Found Saturation Exponent:', this.saturationExponent);
          } else {
            console.log('No Saturation Exponent simulations found');
          }
        },
        error: (error) => {
          console.error('Error loading Saturation Exponent:', error);
          this.saturationExponent = null;
          this.saturationExponentId = null;
        }
      });
  }

  private configureCyclesFormControls(): void {
    this.cycles.forEach((cycle: PcCycle) => {
      const cycleResultExists: boolean = this.isCycleResultExists(cycle);

      if (cycleResultExists) {
        this.getCycleControl(cycle).patchValue(true, { emitEvent: false });
        this.getCycleControl(cycle).enable({ emitEvent: false });
      } else {
        this.getCycleControl(cycle).patchValue(false, { emitEvent: false });
        this.getCycleControl(cycle).disable({ emitEvent: false });
      }
    });
  }

  private initChart(): void {
    // Initialize chart with empty series - series will be added dynamically via updateChartSeriesVisibility
    this.chartOptions = createBaseLinearChartOptions({
      title: 'Capillary Pressure (Pc)',
      xAxisTitle: 'Saturation (fraction)',
      yAxisTitle: 'Capillary Pressure (psi)',
      xMin: 0,
      xMax: 1,
      series: [], // Start with empty series
      tooltipFormatter: function() {
        const xVal = typeof this.x === 'number' ? this.x : 0;
        const yVal = typeof this.y === 'number' ? this.y : 0;
        return `<b>${this.series.name}</b><br/>Saturation: ${xVal.toFixed(2)}<br/>Capillary pressure: ${yVal.toFixed(2)} psi`;
      },
    });

    // Set tick intervals for PC chart
    if (this.chartOptions!.xAxis && Array.isArray(this.chartOptions!.xAxis)) {
      (this.chartOptions!.xAxis as any)[0].tickInterval = 0.1;
      (this.chartOptions!.xAxis as any)[0].minorTickInterval = 0.1;
    } else if (this.chartOptions!.xAxis && typeof this.chartOptions!.xAxis === 'object') {
      (this.chartOptions!.xAxis as any).tickInterval = 0.1;
      (this.chartOptions!.xAxis as any).minorTickInterval = 0.1;
    }

    // Override exporting to be enabled for this component
    this.chartOptions!.exporting = {
      enabled: true,
      fallbackToExportServer: false,
      buttons: {
        contextButton: {
          menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
        },
      },
    };
  }

  private createLinearChart(
    title: string,
    xAxisTitle: string,
    yAxisTitle: string,
  ): Highcharts.Options {
    // This method is deprecated - use initChart() instead
    return this.chartOptions!;
  }

  private initChartSeries(): Highcharts.SeriesOptionsType[] {
    const directionSeries: Highcharts.SeriesOptionsType[] = [];
    const isPDEnabled: boolean = this.getCycleControl('d1').value;
    const isFIEnabled: boolean = this.getCycleControl('imb').value;
    const isSDEnabled: boolean = false; //this.getCycleControl('d2').value;

    if (isPDEnabled) {
      const series: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('d1');
      directionSeries.push(series);
    }

    if (isFIEnabled) {
      const series: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('imb');
      directionSeries.push(series);
    }

    if (isSDEnabled) {
      const series: Highcharts.SeriesOptionsType = this.createChartSeriesByCycleType('d2');
      directionSeries.push(series);
    }

    return directionSeries;
  }

  private createChartSeriesByCycleType(cycle: PcCycle): Highcharts.SeriesOptionsType {
    let data: number[][];
    let name: string;

    switch (cycle) {
      case 'd1':
        name = `Primary drainage (${this.simulationDirection.toUpperCase()})`;
        data = this.swd1Value.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcd1Value;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;

      case 'imb':
        name = `First imbibition (${this.simulationDirection.toUpperCase()})`;
        data = this.swImbValue.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcImbValue;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;

      case 'd2':
        name = `Secondary drainage (${this.simulationDirection.toUpperCase()})`;
        data = this.swd2Value.map((saturation: string, index: number) => {
          const pcValues: string[] = this.pcd2Value;
          return [Number(saturation), Number(pcValues[index])];
        });
        break;
    }

    return createLineSeries({
      name,
      data,
      color: this.defineColorForCycleType(cycle),
      markerEnabled: true,
      custom: {
        cycle,
      },
    });
  }

  private updateSeriesWhenSelectedCyclesChanged(cycle: PcCycle, checked: boolean): void {
    // Regenerate all series in consistent order instead of just adding/removing
    // This ensures series order always matches table order (d1, imb, d2)
    this.updateChartSeriesVisibilitySmooth();
  }

  private updateChartSeriesVisibilitySmooth(): void {
    if (!this.chart) return;

    const cycleOrder: PcCycle[] = ['d1', 'imb', 'd2'];

    // Determine which series should be visible based on selection
    const selectedSeriesNames = new Set<string>();
    cycleOrder.forEach((cycle) => {
      if (this.getCycleControl(cycle).value && this.isCycleResultExists(cycle)) {
        // Get the actual series name from the created series
        const series = this.createChartSeriesByCycleType(cycle);
        selectedSeriesNames.add(series.name as string);
      }
    });

    updateChartSeriesVisibilitySmooth(this.chart, selectedSeriesNames);
  }

  private updateTable(): void {
    const rows: any[] = [];
    const maxLength: number = Math.max(
      this.pcd1Value.length,
      this.swd1Value.length,
      this.pcImbValue.length,
      this.swImbValue.length,
      // this.pcd2Value.length,
      // this.swd2Value.length,
    );

    for (let i = 0; i < maxLength; i++) {
      const row: Record<string, string> = {};

      const direction: string = this.simulationDirection.toUpperCase();
      const isPDEnabled: boolean = this.getCycleControl('d1').value;
      const isFIEnabled: boolean = this.getCycleControl('imb').value;
      const isSDEnabled: boolean = false; // this.getCycleControl('d2').value;

      if (isPDEnabled) {
        row[`PD Saturation (fraction)`] = formatValue(this.swd1Value[i]);
        row[`PD Capillary Pressure (psi)`] = formatValue(this.pcd1Value[i]);
      }

      if (isFIEnabled) {
        row[`FI Saturation (fraction)`] = formatValue(this.swImbValue[i]);
        row[`FI Capillary Pressure (psi)`] = formatValue(this.pcImbValue[i]);
      }

      if (isSDEnabled) {
        row[`SD Saturation (fraction)`] = formatValue(this.swd2Value[i]);
        row[`SD Capillary Pressure (psi)`] = formatValue(this.pcd2Value[i]);
      }

      rows.push(row);
    }

    this.displayedColumns = rows.length ? Object.keys(rows[0]) : [];
    this.tableData = rows;
  }

  private defineColorForCycleType(cycle: PcCycle): string {
    switch (cycle) {
      case 'd1':
        return this.primaryDrainageColor;

      case 'imb':
        return this.firstImbibitionColor;

      case 'd2':
        return this.secondaryDrainageColor;
    }
  }

  private defineColorForTable(columnName: string): string {
    switch (columnName.toLowerCase().slice(0, 2)) {
      case 'pd':
        return this.defineColorForCycleType('d1');

      case 'fi':
        return this.defineColorForCycleType('imb');

      case 'sd':
        return this.defineColorForCycleType('d2');

      default:
        return this.defaultColor;
    }
  }

  private getSelectedDirections(): Record<string, boolean> {
    return {
      directionX: this.simulationDirection.toLowerCase() === 'x',
      directionY: this.simulationDirection.toLowerCase() === 'y',
      directionZ: this.simulationDirection.toLowerCase() === 'z',
    };
  }

  private initCyclesForm(): void {
    this.cyclesForm = this.fb.group({
      d1: [{ value: false, disabled: true }],
      imb: [{ value: false, disabled: true }],
      d2: [{ value: false, disabled: true }],
    });

    merge(
      this.getCycleControl('d1').valueChanges.pipe(map(value => ({ cycle: 'd1', value }))),
      this.getCycleControl('imb').valueChanges.pipe(map(value => ({ cycle: 'imb', value }))),
      this.getCycleControl('d2').valueChanges.pipe(map(value => ({ cycle: 'd2', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ cycle, value }) => {
        this.updateSeriesWhenSelectedCyclesChanged(cycle as PcCycle, value);
        this.updateTable();
      });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }
}
